// CriteriaFinder.jsx
import React, { useMemo, useState } from "react";

/* ---------- helpers ---------- */
const sn = (v) => (typeof v === "string" ? v.trim().toLowerCase() : null);
function num(n) { const v = Number(n); return Number.isFinite(v) ? v : null; }
function bool(b) { return typeof b === "boolean" ? b : null; }

function deriveSide(doc) {
  const rec = (doc?.analysis?.recommendation || "").toUpperCase();
  if (["BUY","STRONG BUY","LONG","STRONG LONG"].some(s => rec.includes(s))) return "LONG";
  if (["SELL","STRONG SELL","SHORT","STRONG SHORT"].some(s => rec.includes(s))) return "SHORT";
  return "NA";
}

function baseSymbol(token = "") {
  const t = token.toUpperCase().replace(/-PERP$/, "");
  if (t.includes("/")) return t.split("/")[0];
  if (t.includes(":")) return t.split(":")[0];
  return t;
}
function isBTCUSDT(token = "") {
  const t = token.toUpperCase();
  return t === "BTC/USDT" || t === "BTCUSDT" || /BTC\/USDT/.test(t);
}

/* ---------- flatten each doc ---------- */
function flattenDoc(d){
  const tf = d?.timeframesUsed || {};
  const daily  = tf.daily     || {};
  const hourly = tf.hourly    || {};
  const short  = tf.shortTerm || {};
  const mc  = d?.marketContext || {};
  const ind = d?.indicators || {};

  return {
    id: d._id,
    token: d.token,
    timestamp: d.timestamp,

    // outcome
    humanLabel: sn(d?.outcome?.humanLabel),         // <— NEW (normalized)
    profitLoss: num(d?.outcome?.profitLoss),
    daysToOutcome: num(d?.outcome?.daysToOutcome),

    // analysis basics
    score: num(d?.analysis?.score),
    confidence: num(d?.analysis?.confidenceLevel),
    priceChange24h: num(d?.analysis?.priceChange24h),
    source: d?.analysis?.source || null,
    side: deriveSide(d),                              // LONG | SHORT | NA
    trendStrength: sn(d?.analysis?.trendStrength),

    // indicators (top-level)
    ind_macd: num(ind.macd),
    ind_macdSignal: num(ind.macdSignal),
    ind_rsi: num(ind.rsi),
    ind_vol: num(ind.vol),
    ind_volMA: num(ind.volMA),

    // market context (categorical)
    aiLabel: sn(mc.aiLabel),
    expDir: sn(mc.expectedDirection24h),
    trendPhase: sn(mc.trendPhase),
    total3Trend: sn(mc.total3Trend),
    total3TrendShort: sn(mc.total3TrendShort),
    usdtDomTrend: sn(mc.usdtDominanceTrend),
    usdtDomTrendShort: sn(mc.usdtDominanceTrendShort),
    ethBTCTrend: sn(mc.ethBTCTrend),
    ethBTCTrendShort: sn(mc.ethBTCTrendShort),
    volumeTopCryptosTrend: sn(mc.volumeTopCryptosTrend),
    volumeTopCryptosTrendShort: sn(mc.volumeTopCryptosTrendShort),

    // timeframe: DAILY
    daily_isPotentialTop: bool(daily.isPotentialTop),
    daily_maBias: sn(daily.maBias),
    daily_macdCross: sn(daily.macdCross),
    daily_rsiTrend: sn(daily.rsiTrend),
    daily_volumeTrend: sn(daily.volumeTrend),

    // timeframe: HOURLY
    hourly_isPotentialTop: bool(hourly.isPotentialTop),
    hourly_maBias: sn(hourly.maBias),
    hourly_macdCross: sn(hourly.macdCross),
    hourly_rsiTrend: sn(hourly.rsiTrend),
    hourly_volumeTrend: sn(hourly.volumeTrend),

    // timeframe: SHORT
    short_isPotentialTop: bool(short.isPotentialTop),
    short_maBias: sn(short.maBias),
    short_macdCross: sn(short.macdCross),
    short_rsiTrend: sn(short.rsiTrend),
    short_volumeTrend: sn(short.volumeTrend),
  };
}

/* ---------- field pool ---------- */
const POOL_DEFAULT = [
  "aiLabel","expDir","trendPhase",
  "total3Trend","total3TrendShort",
  "usdtDomTrend","usdtDomTrendShort",
  "ethBTCTrend","ethBTCTrendShort",
  "volumeTopCryptosTrend","volumeTopCryptosTrendShort",
  "trendStrength","source",

  "daily_maBias","daily_macdCross","daily_rsiTrend","daily_volumeTrend","daily_isPotentialTop",
  "hourly_maBias","hourly_macdCross","hourly_rsiTrend","hourly_volumeTrend","hourly_isPotentialTop",
  "short_maBias","short_macdCross","short_rsiTrend","short_volumeTrend","short_isPotentialTop",
];

const LABEL = (k) => ({
  aiLabel:"aiLabel", expDir:"exp.Dir", trendPhase:"trendPhase",
  total3Trend:"total3Trend", total3TrendShort:"total3TrendShort",
  usdtDomTrend:"usdtDomTrend", usdtDomTrendShort:"usdtDomTrendShort",
  ethBTCTrend:"ethBTCTrend", ethBTCTrendShort:"ethBTCTrendShort",
  volumeTopCryptosTrend:"volTopTrend", volumeTopCryptosTrendShort:"volTopTrendShort",
  trendStrength:"trend", source:"source",
  daily_maBias:"daily.maBias", daily_macdCross:"daily.macdCross",
  daily_rsiTrend:"daily.rsiTrend", daily_volumeTrend:"daily.volTrend", daily_isPotentialTop:"daily.isTop",
  hourly_maBias:"hourly.maBias", hourly_macdCross:"hourly.macdCross",
  hourly_rsiTrend:"hourly.rsiTrend", hourly_volumeTrend:"hourly.volTrend", hourly_isPotentialTop:"hourly.isTop",
  short_maBias:"short.maBias", short_macdCross:"short.macdCross",
  short_rsiTrend:"short.rsiTrend", short_volumeTrend:"short.volTrend", short_isPotentialTop:"short.isTop",
}[k] || k);

/* ---------- combos + grouping ---------- */
function* combos(arr, k, start=0, prefix=[]){
  if (k === 0) { yield prefix; return; }
  for (let i = start; i <= arr.length - k; i++){
    yield* combos(arr, k-1, i+1, prefix.concat(arr[i]));
  }
}
function groupByFields(rows, fields){
  const map = new Map();
  for (const r of rows){
    let vals = [];
    let hasNull = false;
    for (const f of fields){
      const v = r[f];
      if (v === null || v === undefined) { hasNull = true; break; }
      vals.push(v);
    }
    if (hasNull) continue;
    const key = JSON.stringify(vals);
    if (!map.has(key)) map.set(key, { key, vals, rows: [] });
    map.get(key).rows.push(r);
  }
  return map;
}

/* ---------- humanLabel-based win/loss ---------- */
function labelAsWinOrLoss(row) {
  const h = row.humanLabel;       // normalized
  const s = row.side;             // LONG | SHORT | NA
  if (s === "LONG") {
    if (h === "correct_buy") return "win";
    if (h === "should_sell")  return "loss";
  } else if (s === "SHORT") {
    if (h === "correct_sell") return "win";
    if (h === "should_buy")   return "loss";
  }
  return "other";
}

/* ---------- helpers used by the finder ---------- */
function buildIndex(rows, poolFields, sideWanted) {
  // (field::value) -> Int32Array of row indices (sorted)
  const idx = new Map();
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (sideWanted !== "ANY" && r.side !== sideWanted) continue;
    // we only index rows we can classify into win/loss
    const tag = labelAsWinOrLoss(r);
    if (tag !== "win" && tag !== "loss") continue;

    for (const f of poolFields) {
      const v = r[f];
      if (v === null || v === undefined) continue;
      const key = f + "::" + String(v);
      let arr = idx.get(key);
      if (!arr) { arr = []; idx.set(key, arr); }
      arr.push(i);
    }
  }
  // finalize
  for (const [k, arr] of idx) {
    arr.sort((a,b) => a - b);
    idx.set(k, Int32Array.from(arr));
  }
  return idx;
}

function intersectSorted(a, b) {
  let i = 0, j = 0;
  const out = [];
  while (i < a.length && j < b.length) {
    const x = a[i], y = b[j];
    if (x === y) { out.push(x); i++; j++; }
    else if (x < y) i++;
    else j++;
  }
  return Int32Array.from(out);
}

/* ---------- DROP-IN REPLACEMENT ---------- */
function findPerfectCriteria({
  rows,
  sideWanted,         // "ANY" | "LONG" | "SHORT"
  resultWanted,       // "win" | "loss"
  poolFields,         // string[]
  maxDepth,           // up to 8 (or more)
  minCount,           // e.g., 10
  maxResults = 100,   // UI cap
  maxCombos = 100000, // work budget
  timeBudgetMs = 600  // optional: limit CPU time per run
}) {
  const start = performance.now?.() ?? Date.now();

  // Build inverted index once
  const idx = buildIndex(rows, poolFields, sideWanted);

  // Per-field available values (with support counts) from the index
  const valuesPerField = {};
  for (const f of poolFields) valuesPerField[f] = [];
  for (const [key, arr] of idx) {
    const [f, val] = key.split("::");
    valuesPerField[f]?.push({ key, val, support: arr.length });
  }
  // Remove low-support value buckets up front (prune noise)
  for (const f of poolFields) {
    valuesPerField[f] = (valuesPerField[f] || []).filter(v => v.support >= minCount);
  }
  // Order fields by increasing cardinality (more selective first)
  const orderedFields = [...poolFields]
    .filter(f => (valuesPerField[f]?.length || 0) > 0)
    .sort((a,b) => valuesPerField[a].length - valuesPerField[b].length);

  const results = [];
  let combosTried = 0;

  // DFS state
  function dfs(depth, fieldStartIdx, chosenFields, chosenKeys, matchIdx) {
    // time / work budgets
    if (++combosTried > maxCombos) return;
    if (performance.now && performance.now() - start > timeBudgetMs) return;

    // If we already have a combo (depth > 0), evaluate it.
    if (depth > 0) {
      if (matchIdx.length >= minCount) {
        // Check perfect win/loss
        let ok = true, wins = 0, losses = 0, plSum = 0, plCount = 0;
        for (let i = 0; i < matchIdx.length; i++) {
          const r = rows[matchIdx[i]];
          if (sideWanted !== "ANY" && r.side !== sideWanted) { ok = false; break; }
          const tag = labelAsWinOrLoss(r);
          if (tag !== resultWanted) { ok = false; break; }
          if (tag === "win") wins++;
          else if (tag === "loss") losses++;
          if (typeof r.profitLoss === "number") { plSum += r.profitLoss; plCount++; }
        }
        if (ok) {
          results.push({
            fields: chosenFields.slice(),
            values: chosenKeys.map(k => k.split("::")[1]),
            total: matchIdx.length,
            wins, losses,
            avgPL: plCount ? plSum / plCount : 0,
            rows: Array.from(matchIdx, i => rows[i]),
          });
          if (results.length >= maxResults) return;
        }
      }
      if (depth >= maxDepth) return;
    }

    // Try to extend with another field=value
    for (let fi = fieldStartIdx; fi < orderedFields.length; fi++) {
      const f = orderedFields[fi];
      const buckets = valuesPerField[f];
      if (!buckets?.length) continue;

      for (const { key, support } of buckets) {
        if (support < minCount) continue;
        const bucket = idx.get(key);
        if (!bucket) continue;

        const nextMatch = matchIdx ? intersectSorted(matchIdx, bucket) : bucket;
        if (nextMatch.length < minCount) continue;

        chosenFields.push(f);
        chosenKeys.push(key);
        dfs(depth + 1, fi + 1, chosenFields, chosenKeys, nextMatch);
        chosenFields.pop();
        chosenKeys.pop();

        if (results.length >= maxResults) return;
        if (performance.now && performance.now() - start > timeBudgetMs) return;
        if (combosTried > maxCombos) return;
      }
    }
  }

  // Kick off search
  dfs(0, 0, [], [], null);

  // sort: larger support first, then better avg PL
  results.sort((a,b) => b.total - a.total || b.avgPL - a.avgPL);

  return { results, combosTried, maxCombos, timeMs: (performance.now?.() ?? Date.now()) - start };
}



/* ---------- UI ---------- */
export default function CriteriaFinder({ docs = [] }){
  const [sideWanted, setSideWanted] = useState("ANY");       // ANY | LONG | SHORT
  const [resultWanted, setResultWanted] = useState("win");   // win | loss
  const [minCount, setMinCount] = useState(10);
  const [maxDepth, setMaxDepth] = useState(2);
  const [pool, setPool] = useState(new Set(POOL_DEFAULT));
  const [fieldSearch, setFieldSearch] = useState("");
  const [maxResults, setMaxResults] = useState(50);
  const [excludeBTCExact, setExcludeBTCExact] = useState(true);
  const [excludeBTCBase, setExcludeBTCBase]   = useState(false);
  const [maxCombos, setMaxCombos] = useState(50000);

  const rows = useMemo(() => {
    let list = docs;
    if (excludeBTCBase)   list = list.filter(d => baseSymbol(d.token) !== "BTC");
    else if (excludeBTCExact) list = list.filter(d => !isBTCUSDT(d.token));
    return list.map(flattenDoc);
  }, [docs, excludeBTCExact, excludeBTCBase]);

  const poolList = useMemo(() => {
    const all = Array.from(new Set(POOL_DEFAULT));
    return all.filter(k => LABEL(k).toLowerCase().includes(fieldSearch.toLowerCase()));
  }, [fieldSearch]);

const { results, combosTried, timeMs } = useMemo(() => findPerfectCriteria({
  rows,
  sideWanted,
  resultWanted,                // "win" | "loss"
  poolFields: Array.from(pool),
  maxDepth,                    // allow up to 8
  minCount: Number(minCount) || 1,
  maxResults: Number(maxResults) || 100,
  maxCombos: Number(maxCombos) || 100000,
  timeBudgetMs: 1000,          // optional: ~1s compute budget
}), [rows, sideWanted, resultWanted, pool, maxDepth, minCount, maxResults, maxCombos]);



  const togglePool = (k) => {
    const next = new Set(pool);
    if (next.has(k)) next.delete(k); else next.add(k);
    setPool(next);
  };

  return (
    <div className="cf">
      <div className="cf-controls">
        <h3>Perfect Criteria Finder</h3>

        <div className="cf-row">
          <label>Side</label>
          <select value={sideWanted} onChange={(e)=>setSideWanted(e.target.value)}>
            <option>ANY</option>
            <option>LONG</option>
            <option>SHORT</option>
          </select>

          <label>Result</label>
          <select value={resultWanted} onChange={(e)=>setResultWanted(e.target.value)}>
            <option value="win">Wins</option>
            <option value="loss">Losses</option>
          </select>
          <div/>

        <div className="cf-row">
          <label>Min count</label>
          <input type="number" value={minCount} min={1} onChange={(e)=>setMinCount(e.target.value)} />

            <label>Max depth</label>
            <select value={maxDepth} onChange={(e)=>setMaxDepth(Number(e.target.value))}>
            {[1,2,3,4,5,6,7,8].map(n => (
                <option key={n} value={n}>{n} {n===1?'field':'fields'}</option>
            ))}
            </select>

          <label>Max results</label>
          <input type="number" value={maxResults} min={1} onChange={(e)=>setMaxResults(e.target.value)} />
        </div>


      <div className="cf-row">
        <label>Max combos</label>
            <input
            type="number"
            min={1000}
            step={1000}
            value={maxCombos}
            onChange={(e)=>setMaxCombos(Number(e.target.value) || 50000)}
            />
         </div>
        </div>

        <div className="cf-row" >
          <label>Exclude</label>
          <label className="cf-toggle">
            <input className="checkbox"
              type="checkbox"
              checked={excludeBTCExact}
              onChange={(e)=> setExcludeBTCExact(e.target.checked)}
              disabled={excludeBTCBase}
            />
            <span>BTC/USDT only</span>
          </label>
          <label className="cf-toggle">
            <input className="checkbox"
              type="checkbox"
              checked={excludeBTCBase}
              onChange={(e)=> setExcludeBTCBase(e.target.checked)}
            />
            <span>All BTC base</span>
          </label>
        </div>

        <div className="cf-pool">
          <div className="cf-row">
            <div className="cf-pool-title">Search Fields</div>
            <input
              placeholder="Filter fields…"
              value={fieldSearch}
              onChange={(e)=>setFieldSearch(e.target.value)}
            />
            <div className="cf-pool-actions">
              <button onClick={()=>setPool(new Set(poolList))}>Select shown</button>
              <button onClick={()=>setPool(new Set())}>Clear</button>
              <button onClick={()=>setPool(new Set(POOL_DEFAULT))}>Default</button>
            </div>
          </div>

          <div className="cf-pool-grid">
            {poolList.map(k => (
              <label key={k} className="cf-pool-opt">
                <input className="checkbox" type="checkbox" checked={pool.has(k)} onChange={()=>togglePool(k)} />
                <span>{LABEL(k)}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="cf-results">
        <div className="cf-results-head">
        Found <strong>{results.length}</strong> perfect criteria set{results.length===1?"":"s"}
        {results.length >= maxResults && <span className="cf-muted"> (capped by max results)</span>}
        <span className="cf-muted"> • depth up to {maxDepth}, budget {maxCombos.toLocaleString()} combos</span>
        <span className="cf-muted"> • tried {combosTried.toLocaleString()} in {Math.round(timeMs)} ms</span>
        </div>



        <div className="cf-cards">
          {results.map((res, i) => (
            <div key={i} className="cf-card">
              <div className="cf-card-head">
                <div className="cf-card-title">
                  Criteria
                  {res.fields.map((f, idx) => (
                    <span key={idx} className="cf-chip">{LABEL(f)} = <b>{String(res.values[idx])}</b></span>
                  ))}
                </div>
                <div className="cf-card-metrics">
                  <span className="cf-badge">Total: {res.total}</span>
                  <span className={`cf-badge ${resultWanted==="win"?"is-green":"is-red"}`}>
                    {resultWanted === "win" ? `Wins: ${res.wins}` : `Losses: ${res.losses}`}
                  </span>
                  <span className="cf-badge">Avg P/L: {res.avgPL.toFixed(2)}%</span>
                </div>
              </div>

              <div className="cf-tablewrap">
                <table className="cf-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Token</th>
                      <th>Side</th>
                      <th>Label</th>
                      <th>P/L%</th>
                      <th>Score</th>
                      <th>Trend</th>
                      <th>aiLabel</th>
                      <th>exp.Dir</th>
                      <th>daily.maBias</th>
                      <th>hourly.maBias</th>
                      <th>short.maBias</th>
                      <th>daily.macdCross</th>
                      <th>hourly.macdCross</th>
                      <th>short.macdCross</th>
                    </tr>
                  </thead>
                  <tbody>
                    {res.rows.map(r => (
                      <tr key={r.id}>
                        <td>{r.timestamp ? new Date(r.timestamp).toLocaleString() : "-"}</td>
                        <td>{r.token}</td>
                        <td>{r.side}</td>
                        <td>{r.humanLabel ?? "-"}</td>
                        <td>{typeof r.profitLoss === "number" ? r.profitLoss.toFixed(2) : "-"}</td>
                        <td>{r.score ?? "-"}</td>
                        <td>{r.trendStrength ?? "-"}</td>
                        <td>{r.aiLabel ?? "-"}</td>
                        <td>{r.expDir ?? "-"}</td>
                        <td>{r.daily_maBias ?? "-"}</td>
                        <td>{r.hourly_maBias ?? "-"}</td>
                        <td>{r.short_maBias ?? "-"}</td>
                        <td>{r.daily_macdCross ?? "-"}</td>
                        <td>{r.hourly_macdCross ?? "-"}</td>
                        <td>{r.short_macdCross ?? "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {results.length === 0 && (
            <div className="cf-empty">
              No perfect sets with the current constraints. Try lowering min count/depth or adjusting the field pool.
            </div>
          )}
        </div>
      </div>

      {/* minimal styles (move to your CSS if you prefer) */}
      <style>{`
        .cf { display:grid; grid-template-columns: 360px 1fr; gap:16px; height:72vh; min-height:540px; }
        @media (max-width: 1000px){ .cf { grid-template-columns: 1fr; height: auto; } }
        .cf-controls { overflow:auto; padding:12px; border:1px solid #243149; border-radius:12px; background:#0b0f1a; }
        .cf-results  { overflow:auto; padding:12px; border:1px solid #243149; border-radius:12px; background:#0b0f1a; }
        .cf-row { display:grid; grid-template-columns: repeat(1, minmax(0,1fr)); gap:8px; margin:8px 0 12px; }
        .cf-row label { align-self:center; font-size:12px; opacity:.85; }
        .cf-pool-head { display:grid; grid-template-columns: auto 1fr auto auto auto; gap:8px; align-items:center; margin-bottom:8px; }
        .cf-pool-title { font-weight:600; }
        .cf-pool-actions button { margin-left:6px; }
        .cf-pool-grid { display:grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap:6px 12px; }
        .cf-pool-opt { display:flex; gap:8px; align-items:center; font-size:12px; }
        .checkbox { max-width:16px}
        .cf-results-head { margin-bottom:8px; }
        .cf-muted { opacity:.7; }
        .cf-cards { display:flex; flex-direction:column; gap:14px; }
        .cf-card { border:1px solid #1b2740; border-radius:12px; overflow:hidden; }
        .cf-card-head { display:flex; justify-content:space-between; gap:8px; padding:10px; background:#0e1524; border-bottom:1px solid #1b2740; }
        .cf-card-title { display:flex; gap:6px; align-items:center; flex-wrap:wrap; }
        .cf-chip { border:1px solid #344463; background:#0d1320; color:#dce6ff; padding:2px 8px; border-radius:999px; font-size:12px; }
        .cf-card-metrics { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
        .cf-badge { border:1px solid #2b3b58; background:#10192b; color:#dbe6ff; padding:3px 8px; border-radius:8px; font-size:12px; }
        .cf-badge.is-green { border-color:#2b6448; background:#123023; }
        .cf-badge.is-red   { border-color:#6b2b2b; background:#301212; }
        .cf-tablewrap { overflow:auto; }
        .cf-table { width:max-content; min-width:100%; border-collapse:collapse; }
        .cf-table th, .cf-table td { padding:6px 8px; border-bottom:1px solid #1a263e; white-space:nowrap; }
        .cf-empty { opacity:.8; padding:10px; }
        .cf-controls input, .cf-controls select { padding:6px 8px; background:#0d1320; border:1px solid #2a3852; color:#e4ecfa; border-radius:8px; }
        .cf-controls button { padding:6px 8px; border-radius:8px; border:1px solid #2b3b58; background:#10192b; color:#dbe6ff; cursor:pointer; }
        .cf-controls button:hover { filter:brightness(1.08); }
      `}</style>
    </div>
  );
}
